import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { translationUpdate } from 'redux/actions/users';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import EditableTitleWithDescriptionStyled from './base/editable-title-and-description-styled';


type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditableFeedbackForAssignment = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.PUBLIC_PEER_EVALUATION | ComponentType.PRIVATE_PEER_EVALUATION>;
  const peer = useSelector((state) => state.models.peerEvaluations[lectureComponent.peerEvaluation]);
  const initialState = {
    title: peer.title,
    description: peer.description,
  };
  const [activity, setActivity] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableFeedbackStyle = css`
    width: 100%;
  `;

  const save = useCallback(() => {
    const { id } = editInfo;
    const attributes = [];
    const fields = Object.keys(activity);
    fields.forEach(field => {
      if (activity[field] !== initialState[field]) {
        attributes.push({
          translationId: peer.translationIds[field],
          name: field,
          text: activity[field],
          elementType: lectureComponent.type,
          elementId: lectureComponent.peerEvaluation,
        });
      }
    });
    const payload = {
      id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [activity, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== activity) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [activity]);


  return (
    <div className='editable-feedback' css={editableFeedbackStyle}>
      <EditableTitleWithDescriptionStyled
        activity={activity}
        setActivity={setActivity}
        containerClass={containerClass}
        titleName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE()}
        descriptionName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.DESCRIPTION()}
      />
    </div>
  );
};

export default EditableFeedbackForAssignment;
